import { motion } from 'framer-motion';
import { Shield, Clock, Users, Award, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { companyInfo } from '@/data/index';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { useLanguage } from '@/lib/language';

export default function About() {
  const { t } = useLanguage();
  
  const valueIcons = {
    'Bezpieczeństwo': Shield,
    'Transparentność': Award,
    'Profesjonalizm': Users,
    'Szybkość': Clock,
  };

  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.EXPRESS_HUB_TEAM_SERVICE}
            alt="EXPRESS HUB Team"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              {companyInfo.name}
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              {t('about.subtitle')}
            </p>
            <p className="text-lg text-foreground/80 leading-relaxed">
              {t('about.description')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('about.mission.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('about.mission.description')}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="grid md:grid-cols-2 gap-8 mb-16"
            >
              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{t('about.values.security')}</h3>
                </div>
                <p className="text-muted-foreground">
                  {t('home.features.security.description')}
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{t('about.values.reliability')}</h3>
                </div>
                <p className="text-muted-foreground">
                  {t('about.mission.description')}
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{t('about.values.speed')}</h3>
                </div>
                <p className="text-muted-foreground">
                  {t('home.features.speed.description')}
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">{t('about.values.trust')}</h3>
                </div>
                <p className="text-muted-foreground">
                  {t('home.features.support.description')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={staggerItem} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('about.team.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('about.team.description')}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
            >
              {companyInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground">
                    {t(`home.stats.${
                      stat.label.includes('przesyłek') ? 'deliveries' :
                      stat.label.includes('Krajów') ? 'countries' :
                      stat.label.includes('klientów') ? 'satisfaction' :
                      'experience'
                    }`)}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div variants={staggerItem} className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('about.contact.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('about.contact.description')}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="grid md:grid-cols-3 gap-8"
            >
              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('common.phone')}</h3>
                <p className="text-muted-foreground mb-2">
                  Polska: +48 22 263 91 91<br/>
                  Szwajcaria: +41 415 331 634
                </p>
                <div className="space-y-1">
                  <a 
                    href="tel:+48222639191"
                    className="text-primary hover:underline text-sm block"
                  >
                    {t('contact.callNow')} (PL)
                  </a>
                  <a 
                    href="tel:+41415331634"
                    className="text-primary hover:underline text-sm block"
                  >
                    {t('contact.callNow')} (CH)
                  </a>
                </div>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('common.email')}</h3>
                <p className="text-muted-foreground mb-2">{companyInfo.contact.email}</p>
                <a 
                  href={`mailto:${companyInfo.contact.email}`}
                  className="text-primary hover:underline text-sm"
                >
                  {t('tracking.emailButton')}
                </a>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('common.address')}</h3>
                <p className="text-muted-foreground text-sm">
                  {companyInfo.contact.address}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}